/**
 * BulkExportStatusPage Component
 * Job status tracking page with auto-refresh and download
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { usePageMeta } from '../../lib/usePageMeta';
import Button from '../ui/Button';

interface BulkExportJob {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_count: number;
  processed_count: number;
  progress_percent: number;
  created_at: string;
  completed_at?: string;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

const BulkExportStatusPage: React.FC = () => {
  usePageMeta({ title: 'Export Status', description: 'Track the status of your bulk data export job.', path: '/bulk-status' });
  const searchParams = useSearch({ from: '/bulk-status' }) as { job_id?: string };
  const navigate = useNavigate();
  const jobId = searchParams.job_id;

  const { data, error, isLoading } = useQuery<{ job: BulkExportJob; elapsed_time_formatted: string }, Error>({
    queryKey: ['bulk-export', jobId],
    queryFn: async () => {
      if (!jobId) throw new Error('No job ID provided');
      const res = await fetch(`${API_URL}/bulk-export/${jobId}`);
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error || 'Failed to fetch status');
      }
      return res.json();
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.job?.status;
      return status === 'completed' || status === 'failed' ? false : 5000;
    },
  });

  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-slate-900 py-12 flex justify-center items-center">
        <div className="animate-spin text-sky-500">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  const downloadMutation = async () => {
    if (!jobId) return;
    try {
      // Direct download or fetch presigned URL
      // Assuming direct link for now or simpler handler
      const res = await fetch(`${API_URL}/bulk-export/${jobId}/download`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
      alert('Download failed');
    }
  };

  const job = data?.job;
  const elapsedFormatted = data?.elapsed_time_formatted || '0s';

  // Calculate progress
  const progressPercent = job?.progress_percent || 0;
  const processedCount = job?.processed_count || 0;
  const totalCount = job?.total_count || 0;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'failed': return 'text-red-500';
      case 'processing': return 'text-sky-500';
      case 'pending': return 'text-yellow-500';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'processing':
        return (
          <div className="animate-spin text-sky-500">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        );
      default:
        return (
          <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (!jobId) {
    return (
      <div className="min-h-screen bg-slate-900 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">No Job ID Provided</h2>
            <Button variant="primary" onClick={() => navigate({ to: '/bulk-export' })}>
              Create New Export
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Bulk Export Status</h1>
          <p className="text-lg text-slate-400">Job ID: <span className="font-mono text-sky-400">{jobId}</span></p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 mb-6">
            <p className="text-red-400">Error: {(error as Error).message}</p>
          </div>
        )}

        {/* Status Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              {getStatusIcon(job?.status)}
              <div>
                <p className="text-sm text-slate-400">Status</p>
                <p className={`text-2xl font-bold capitalize ${getStatusColor(job?.status)}`}>
                  {job?.status || 'Loading...'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Elapsed Time</p>
              <p className="text-2xl font-bold text-slate-200">{elapsedFormatted}</p>
            </div>
          </div>

          {/* Progress Bar */}
          {job?.status === 'processing' && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-300">Processing Progress</p>
                <p className="text-sm font-medium text-sky-400">{progressPercent.toFixed(1)}%</p>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 bg-gradient-to-r from-sky-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-slate-400">
                  Processed: <strong className="text-slate-300">{processedCount.toLocaleString()}</strong> / <strong className="text-slate-300">{totalCount.toLocaleString()}</strong> records
                </p>
              </div>
            </div>
          )}

          {/* Completed State */}
          {job?.status === 'completed' && (
            <div className="bg-green-900/20 border border-green-500 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-green-400 mb-1">Export Complete!</p>
                  <p className="text-sm text-slate-300">
                    Successfully processed <strong>{totalCount.toLocaleString()}</strong> records
                  </p>
                </div>
                <Button variant="primary" size="lg" onClick={downloadMutation}>
                  Download CSV
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <Button variant="secondary" onClick={() => navigate({ to: '/bulk-export' })}>
              Create Another Export
            </Button>
            <Button variant="ghost" onClick={() => navigate({ to: '/search' })}>
              Back to Search
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkExportStatusPage;
